import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    User,
    Building,
    Bell,
    Loader2,
    Mail,
    Phone,
    MapPin,
    Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { landlordApi } from '@/lib/api';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';

type Tab = 'personal' | 'business' | 'notifications';

export default function LandlordProfilePage() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<Tab>('personal');
    const [isEditing, setIsEditing] = useState(false);

    // Fetch Profile
    const { data: profile, isLoading } = useQuery({
        queryKey: ['landlordProfile'],
        queryFn: async () => {
            const res = await landlordApi.getMyProfile();
            return res.data;
        },
    });

    // Update Mutation
    const updateProfile = useMutation({
        mutationFn: (data: any) => landlordApi.updateProfile(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['landlordProfile'] });
            toast.success('Profile updated successfully');
            setIsEditing(false);
        },
        onError: (err: any) => {
            toast.error(err.response?.data?.message || 'Failed to update profile');
        },
    });

    if (isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
        );
    }

    if (!profile) return null;

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage your personal information and preferences
                    </p>
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-4">
                {/* Sidebar Nav */}
                <div className="space-y-1 lg:col-span-1">
                    <NavButton
                        active={activeTab === 'personal'}
                        onClick={() => setActiveTab('personal')}
                        icon={User}
                        label="Personal Information"
                    />
                    <NavButton
                        active={activeTab === 'business'}
                        onClick={() => setActiveTab('business')}
                        icon={Building}
                        label="Business Details"
                    />
                    <NavButton
                        active={activeTab === 'notifications'}
                        onClick={() => setActiveTab('notifications')}
                        icon={Bell}
                        label="Notifications"
                    />
                </div>

                {/* Content Area */}
                <div className="rounded-xl border border-gray-200 bg-white shadow-sm lg:col-span-3">
                    {activeTab === 'personal' && (
                        <PersonalForm
                            profile={profile}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            onSubmit={(data: any) => updateProfile.mutate(data)}
                            isSaving={updateProfile.isPending}
                        />
                    )}

                    {activeTab === 'business' && (
                        <BusinessForm
                            profile={profile}
                            isEditing={isEditing}
                            setIsEditing={setIsEditing}
                            onSubmit={(data: any) => updateProfile.mutate(data)}
                            isSaving={updateProfile.isPending}
                        />
                    )}

                    {activeTab === 'notifications' && (
                        <NotificationsForm
                            profile={profile}
                            readOnly={true}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function NavButton({ active, onClick, icon: Icon, label }: any) {
    return (
        <button
            onClick={onClick}
            className={cn(
                "flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors",
                active
                    ? "bg-primary-50 text-primary-700 hover:bg-primary-100"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
            )}
        >
            <Icon className="h-5 w-5" />
            {label}
        </button>
    );
}

function PersonalForm({ profile, isEditing, setIsEditing, onSubmit, isSaving }: any) {
    const [formData, setFormData] = useState({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || '',
    });

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Personal Information</h2>
                {!isEditing ? (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>Edit</Button>
                ) : (
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                        <Button size="sm" onClick={() => onSubmit(formData)} disabled={isSaving}>
                            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </div>
                )}
            </div>

            <div className="space-y-4 max-w-lg">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input
                            disabled={!isEditing}
                            value={formData.firstName}
                            onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input
                            disabled={!isEditing}
                            value={formData.lastName}
                            onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-500">
                        <Mail className="h-4 w-4" />
                        <span className="text-sm">{profile.email}</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed directly.</p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="relative">
                        <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <input
                            disabled={!isEditing}
                            value={formData.phone}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm disabled:bg-gray-50"
                            placeholder="+1 (555) 000-0000"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function BusinessForm({ profile, isEditing, setIsEditing, onSubmit, isSaving }: any) {
    const [formData, setFormData] = useState({
        businessName: profile.businessName || '',
        businessNumber: profile.businessNumber || '',
        address: {
            line1: profile.address?.line1 || '',
            line2: profile.address?.line2 || '',
            city: profile.address?.city || '',
            province: profile.address?.province || '',
            postalCode: profile.address?.postalCode || '',
            country: profile.address?.country || '',
        }
    });

    const updateAddress = (field: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            address: { ...prev.address, [field]: value }
        }));
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Business Details</h2>
                {!isEditing ? (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>Edit</Button>
                ) : (
                    <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                        <Button size="sm" onClick={() => onSubmit(formData)} disabled={isSaving}>
                            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save
                        </Button>
                    </div>
                )}
            </div>

            <div className="space-y-6 max-w-lg">
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900 border-b pb-2">
                        <Briefcase className="h-4 w-4 text-gray-500" />
                        Company Info
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                        <input
                            disabled={!isEditing}
                            value={formData.businessName}
                            onChange={e => setFormData({ ...formData, businessName: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            placeholder="e.g. Acme Properties Inc."
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Number (BN)</label>
                        <input
                            disabled={!isEditing}
                            value={formData.businessNumber}
                            onChange={e => setFormData({ ...formData, businessNumber: e.target.value })}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                        />
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-900 border-b pb-2">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        Business Address
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                        <input
                            disabled={!isEditing}
                            value={formData.address.line1}
                            onChange={e => updateAddress('line1', e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Suite / Unit</label>
                        <input
                            disabled={!isEditing}
                            value={formData.address.line2}
                            onChange={e => updateAddress('line2', e.target.value)}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                            <input
                                disabled={!isEditing}
                                value={formData.address.city}
                                onChange={e => updateAddress('city', e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
                            <input
                                disabled={!isEditing}
                                value={formData.address.province}
                                onChange={e => updateAddress('province', e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                            <input
                                disabled={!isEditing}
                                value={formData.address.postalCode}
                                onChange={e => updateAddress('postalCode', e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                            <input
                                disabled={!isEditing}
                                value={formData.address.country}
                                onChange={e => updateAddress('country', e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function NotificationsForm({ profile, readOnly }: any) {
    const prefs = profile.notificationPreferences;
    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
                {readOnly && <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Read Only</span>}
            </div>

            <div className="space-y-6">
                <NotificationSection title="Channels" items={[
                    { label: 'Email Notifications', checked: prefs.emailEnabled },
                    { label: 'Push Notifications', checked: prefs.pushEnabled },
                    { label: 'SMS Notifications', checked: prefs.smsEnabled },
                ]} />

                <NotificationSection title="Alerts" items={[
                    { label: 'Rent Due Reminders', checked: prefs.rentDueReminders },
                    { label: 'Late Payment Alerts', checked: prefs.latePaymentAlerts },
                    { label: 'Payment Received', checked: prefs.paymentReceived },
                ]} />

                <NotificationSection title="Maintenance" items={[
                    { label: 'New Requests', checked: prefs.maintenanceRequestNew },
                    { label: 'Quote Received', checked: prefs.maintenanceQuoteReceived },
                    { label: 'Job Scheduled', checked: prefs.maintenanceScheduled },
                    { label: 'Job Completed', checked: prefs.maintenanceCompleted },
                ]} />
            </div>
        </div>
    );
}

function NotificationSection({ title, items }: any) {
    return (
        <div>
            <h3 className="text-sm font-medium text-gray-900 mb-3">{title}</h3>
            <div className="space-y-3">
                {items.map((item: any, i: number) => (
                    <div key={i} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <div className={cn("h-5 w-9 rounded-full relative transition-colors", item.checked ? "bg-primary-600" : "bg-gray-200")}>
                            <div className={cn("absolute top-1 left-1 h-3 w-3 rounded-full bg-white transition-transform", item.checked ? "translate-x-4" : "")} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
